export const parseRecipients = (recipients) => {
  const validRecipients = []
  const invalidRecipients = []

  if (recipients) {
    recipients.forEach((recipient) => {
      if (recipient.email) {
        validRecipients.push(recipient)
      } else {
        invalidRecipients.push(recipient)
      }
    })
  }
  return [validRecipients, invalidRecipients]
}

export const isLargeRecipientList = (recipients) => {
  return recipients && recipients.length === 1 && recipients[0]?.chapter1 === "largeNumRecpMarker"
}

/**
 * Gets the correct recipient count for both normal and large recipient lists.
 *
 * @param {Array<Object>} recipients - Array of recipient objects
 * @param {string} recipients[].chapter1 - Chapter identifier (set to "largeNumRecpMarker" for large lists)
 * @param {number} recipients[].constituentId - The actual recipient count for large lists
 * @returns {number} The correct recipient count
 *
 * @example
 * // Normal recipient list
 * getRecipientCount([
 *   { email: "user1@example.com", name: "User 1" },
 *   { email: "user2@example.com", name: "User 2" }
 * ]) // Returns: 2
 *
 * @example
 * // Large recipient list (performance optimized)
 * getRecipientCount([
 *   { chapter1: "largeNumRecpMarker", constituentId: 1500 }
 * ]) // Returns: 1500
 */
export const getRecipientCount = (recipients) => {
  if (!recipients) return 0

  // Handle large recipient lists - use constituentId from first object
  if (recipients.length === 1 && recipients[0].chapter1 === "largeNumRecpMarker") {
    return recipients[0].constituentId
  }

  return recipients.length
}

export const validTypes = ["mailMerge", "onboarding", "general", "jobs"]

export const mailMergeTypes = [
  "missing_phone_number",
  "missing_social_profile",
  "missing_career",
  "missing_address",
  "requestClassNote",
  "confirmData",
  "anotherReminder",
  "has not donated",
]

export const mailMergeFields = [
  {
    field: "{first_name}",
    reduxName: "placeholderFirstName",
    name: "First Name",
  },
  { field: "{last_name}", reduxName: "placeholderLastName", name: "Last Name" },
  { field: "{nickname}", reduxName: "placeholderNickName", name: "Nickname" },
  {
    field: "{class_year}",
    reduxName: "placeholderClassYear",
    name: "Class Year",
  },
  {
    field: "{phone_number}",
    reduxName: "placeholderPhoneNumber",
    name: "Phone #",
  },
  {
    field: "{phone_number_type}",
    reduxName: "placeholderPhoneNumberType",
    name: "Phone Type",
  },
  { field: "{address1}", reduxName: "placeholderAddress1", name: "Address 1" },
  { field: "{city}", reduxName: "placeholderCity", name: "City" },
  { field: "{state_code}", reduxName: "placeholderStateCode", name: "State" },
  { field: "{zip_code}", reduxName: "placeholderZipCode", name: "Zip" },
  {
    field: "{country_code}",
    reduxName: "placeholderCountryCode",
    name: "Country",
  },
  { field: "{company}", reduxName: "placeholderCompany", name: "Company" },
  { field: "{title}", reduxName: "placeholderTitle", name: "Title" },
  { field: "{industry}", reduxName: "placeholderIndustry", name: "Industry" },
  {
    field: "{unique_account_url}",
    reduxName: "placeholderUniqueUrl",
    name: "Account URL",
  },
  {
    field: "{last_donation_date}",
    reduxName: "placeholderLastDonationDate",
    name: "Last Gift Date",
  },
  {
    field: "{last_donation_amount}",
    reduxName: "placeholderLastDonationAmount",
    name: "Last Gift Amt",
  },
  {
    field: "{child_info}",
    reduxName: "placeholderChildInfo",
    name: "Child Info",
  },
  {
    field: "{spouse_or_partner_info}",
    reduxName: "placeholderSpouseOrPartnerInfo",
    name: "Spouse or Partner Info",
  },
  {
    field: "{all_family_info}",
    reduxName: "placeholderAllFamilyInfo",
    name: "All Family Info",
  },
  {
    field: "{recipients_shareable_donation_url}",
    reduxName: "placeholderRecipientDonationUrl",
    name: "Recipient's Donation URL",
  },
]

export const defaultMergeFields = mailMergeFields.reduce((mergeDict, item) => {
  const newMergeDict = mergeDict
  newMergeDict[item.reduxName] = ""
  return newMergeDict
}, {})

export const getClassYear = (currentUser, customer) =>
  currentUser?.organizationClassYears[customer.id]

export const getLocation = (currentUser) =>
  currentUser?.location?.city && currentUser?.location?.stateCode
    ? `${currentUser.location.city}, ${currentUser.location.stateCode}`
    : ""

export function getEmailType(adminType, reminder) {
  if (!adminType) {
    return ""
  }
  let type = adminType

  if (reminder) {
    type = "reminder"
  }
  return type
}

export const sortEmailDrafts = (drafts) => {
  return drafts.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
}

const ALLOWED_AS_DRAFTS = new Set(["general", "mailMerge", "classYear"])
export const isAllowedDraftType = (adminEmailType, groupEmailType) =>
  ALLOWED_AS_DRAFTS.has(adminEmailType) || ALLOWED_AS_DRAFTS.has(groupEmailType)
